import React, { useState } from "react";
import { TripResponse } from "../../types/api";
import { useTranslation } from "react-i18next";
import { Box, Button, Card, Typography, CircularProgress } from "@mui/material";
import TractorLicensePlateCellRenderer from "./columnDefComponents/TractorLicensePlateCellRenderer";
import ApprovalCellRenderer from "./columnDefComponents/ApprovalCellRenderer";
import StatusCellRenderer from "./columnDefComponents/StatusCellRenderer";
import { tripCardStyle } from "./style";
import { useBusinessProfile } from "../../utils/hooks/useBusinessProfile";
import { useTripContext } from "../../context/tripsContext";
import { useNavigate } from "react-router-dom";

interface AgGridTripsProps {
  trips: TripResponse[];
  driversMap: Record<string, boolean>;
  isLoadingDrivers: boolean;
  isLoading?: boolean;
}

const MobileCardTrips: React.FC<AgGridTripsProps> = ({ trips, driversMap, isLoadingDrivers, isLoading = false }) => {
  const { t } = useTranslation();
  const [visibleTripsCount, setVisibleTripsCount] = useState(5);
  const {businessProfile} = useBusinessProfile();
  const {setTrips} = useTripContext();
  
  const extractCityName = (location: string): string => {
    if (!location) return "";
    // Se il campo è già solo la città (come loadingCity/unloadingCity), restituiscilo direttamente
    return location.trim();
  };
  const navigate = useNavigate();
  const handleClick = (id: number) => {
    navigate(`/tripdetails/${id}`); // Naviga alla pagina dei dettagli
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months start at 0
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const handleShowMore = () => {
    setVisibleTripsCount((prevCount) => prevCount + 5);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: "200px" }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <div>
      {trips.slice(0, visibleTripsCount).map((trip) => (
        <Card square elevation={2} sx={tripCardStyle.card} key={trip.tripID}>
          <Box style={tripCardStyle.box}>
            <Typography sx={tripCardStyle.tripID}>
              ID: {trip.tmsObjectNumber}
            </Typography>
          </Box>

          <Box sx={tripCardStyle.mainBox}>
            <Typography sx={tripCardStyle.mainText}>
              {t("screens.trips.columnDef.expectedDeparture")}
            </Typography>
            <Typography sx={tripCardStyle.valueText}>
              {formatDate(trip.expectedDeparture)}
            </Typography>
          </Box>

          <Box sx={tripCardStyle.mainBox}>
            <Typography sx={tripCardStyle.mainText}>
              {t("screens.trips.columnDef.expectedArrival")}
            </Typography>
            <Typography sx={tripCardStyle.valueText}>
              {formatDate(trip.expectedArrival)}
            </Typography>
          </Box>

          <Box sx={tripCardStyle.mainBox}>
            <Typography sx={tripCardStyle.mainText}>
              {t("screens.trips.columnDef.loadingLocation")}
            </Typography>
            <Typography sx={tripCardStyle.valueText}>
              {extractCityName(trip.loadingCity)}
            </Typography>
          </Box>

          <Box sx={tripCardStyle.mainBox}>
            <Typography sx={tripCardStyle.mainText}>
              {t("screens.trips.columnDef.unloadingLocation")}
            </Typography>
            <Typography sx={tripCardStyle.valueText}>
              {extractCityName(trip.unloadingCity)}
            </Typography>
          </Box>

          <Box sx={tripCardStyle.box2}>
            <Box sx={tripCardStyle.box3}>
              <Typography sx={tripCardStyle.mainText}>
                {t("screens.trips.columnDef.tractorLicensePlate")}
              </Typography>
              <TractorLicensePlateCellRenderer
                status={trip.status}
                value={trip.tractorLicensePlate}
                tripId={trip.tripID}
                equipmentType="TRUCK"
                user={businessProfile.name}
                onUpdate={(updatedValue) => {
                  // Effettua il refresh della pagina
                  window.location.reload();
                }}
                approvalStatus={trip.approval}
                hasDriver={driversMap[trip.tractorLicensePlate] === true}
              />
            </Box>
            <Box sx={tripCardStyle.box4}>
              <Typography sx={tripCardStyle.mainText}>
                {t("screens.trips.columnDef.trailerLicensePlate")}
              </Typography>
              <TractorLicensePlateCellRenderer
                status={trip.status}
                approvalStatus={trip.approval}
                value={trip.trailerLicensePlate}
                tripId={trip.tripID}
                equipmentType="TRAILER"
                user={businessProfile.name}
                onUpdate={(updatedValue) => {
                  // Effettua il refresh della pagina
                  window.location.reload();
                }}
              />
            </Box>
          </Box>

          <Box sx={tripCardStyle.box2}>
            <Box sx={tripCardStyle.box5}>
              <StatusCellRenderer value={trip.status} />
            </Box>
            <ApprovalCellRenderer
              value={trip.approval}
              tripId={trip?.tripID}
              tmsKey={trip?.tmsObjectNumber}
              tripAcceptance={null}
            />
          </Box>
          <Button variant="text" color="primary" sx={tripCardStyle.button} onClick={() => handleClick(trip.tripID)}>
            {t("screens.trips.showMore")}
          </Button>
        </Card>
      ))}
      {visibleTripsCount < trips.length && (
        <Box sx={{ textAlign: "center", margin:'20px 0' , }}>
          <Button
            variant="text"
            color="primary"
            onClick={handleShowMore}
            sx={{textDecoration:'underline',fontWeight:'bold', fontSize:'14px'}}
          >
            {t("screens.trips.showMoreTrips")}
          </Button>
        </Box>
      )}
    </div>
  );
};

export default MobileCardTrips;
